import { useState, useRef, useMemo } from "react";
import { MirrorlessNote } from "@/components/MirrorlessNote";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  Printer,
  Ruler,
  Image as ImageIcon,
  PaintRoller,
  Palette,
  FileImage,
  CheckSquare,
  Calculator,
  Info,
  Crop,
  Stack,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePDFExport } from "@/utils/pdfExport";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const itemAnim = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const PHOTOS = {
  hero: "/printing/hero.jpg",
  lab: "https://images.unsplash.com/photo-1554080353-a576cf803bda?w=800&q=80",
  frame: "https://images.unsplash.com/photo-1542744094-3a31f272c490?w=800&q=80",
  monitor: "https://images.unsplash.com/photo-1611930022073-b7a4ba5fcccd?w=800&q=80",
};

// Print sizes (inches): w, h and computed pixel requirements at 300/240/180 DPI
const PRINT_SIZES = [
  { w: 4, h: 6, common_en: "Wallet / fridge / prints album", common_es: "Cartera / refrigerador / album" },
  { w: 5, h: 7, common_en: "Desk frame / greeting cards", common_es: "Marco de escritorio / tarjetas" },
  { w: 8, h: 10, common_en: "Family frame / school portraits", common_es: "Marco familiar / retratos escolares" },
  { w: 11, h: 14, common_en: "Gallery print / portfolio book", common_es: "Impresión de galería / portafolio" },
  { w: 16, h: 20, common_en: "Wall art (living room)", common_es: "Arte de pared (sala)" },
  { w: 20, h: 30, common_en: "Feature wall / statement piece", common_es: "Pared principal / pieza destacada" },
  { w: 24, h: 36, common_en: "Poster / gallery showpiece", common_es: "Poster / pieza de galería" },
];

function pxAt(dpi, inches) {
  return Math.round(inches * dpi);
}

// --- SVG: side-by-side aspect ratio comparison ---
function AspectRatioDiagram({ dark = false }) {
  const stroke = dark ? "#3F3F46" : "#9CA3AF";
  const text = dark ? "#E4E4E7" : "#111827";
  const muted = dark ? "#A1A1AA" : "#4B5563";
  const fill3x2 = "#F5A524";
  const fill4x3 = "#60A5FA";
  const fill1x1 = "#A78BFA";
  return (
    <svg viewBox="0 0 520 240" className="w-full h-auto" role="img" aria-label="Aspect ratios">
      {/* 3:2 (DSLR) */}
      <g transform="translate(20,40)">
        <rect width="180" height="120" fill={fill3x2} opacity="0.15" stroke={stroke} />
        <text x="90" y="64" textAnchor="middle" fontSize="18" fontWeight="bold" fill={text}>3:2</text>
        <text x="90" y="82" textAnchor="middle" fontSize="10" fill={muted}>DSLR / Mirrorless</text>
        <text x="90" y="140" textAnchor="middle" fontSize="10" fill={muted}>4x6, 8x12, 12x18, 20x30</text>
      </g>
      {/* 4:3 (M4/3, phone, compact) */}
      <g transform="translate(220,50)">
        <rect width="140" height="105" fill={fill4x3} opacity="0.15" stroke={stroke} />
        <text x="70" y="56" textAnchor="middle" fontSize="18" fontWeight="bold" fill={text}>4:3</text>
        <text x="70" y="74" textAnchor="middle" fontSize="10" fill={muted}>Phone / M4/3</text>
        <text x="70" y="130" textAnchor="middle" fontSize="10" fill={muted}>5x7 close fit</text>
      </g>
      {/* 1:1 */}
      <g transform="translate(390,60)">
        <rect width="100" height="100" fill={fill1x1} opacity="0.15" stroke={stroke} />
        <text x="50" y="56" textAnchor="middle" fontSize="16" fontWeight="bold" fill={text}>1:1</text>
        <text x="50" y="72" textAnchor="middle" fontSize="10" fill={muted}>Square / IG</text>
        <text x="50" y="135" textAnchor="middle" fontSize="10" fill={muted}>5x5, 8x8, 12x12</text>
      </g>
      <text x="260" y="25" textAnchor="middle" fontSize="12" fontWeight="bold" fill={text}>
        Camera Aspect Ratios vs Common Print Sizes
      </text>
    </svg>
  );
}

// --- SVG: Crop planning for 8x10 vs 3:2 sensor ---
function CropDiagram({ dark = false, legend }) {
  const stroke = dark ? "#3F3F46" : "#9CA3AF";
  const text = dark ? "#E4E4E7" : "#111827";
  const muted = dark ? "#A1A1AA" : "#4B5563";
  return (
    <svg viewBox="0 0 520 220" className="w-full h-auto" role="img" aria-label="Crop planning">
      {/* 3:2 frame */}
      <rect x="30" y="30" width="300" height="200" fill="#F5A524" opacity="0.08" stroke={stroke} strokeWidth="1.5" />
      <text x="180" y="22" textAnchor="middle" fontSize="12" fontWeight="bold" fill={text}>
        {legend.frame_label}
      </text>
      {/* 4:5 (8x10) inset - centered */}
      <rect x="80" y="30" width="200" height="200" fill="none" stroke="#F5A524" strokeWidth="2" strokeDasharray="6 3" />
      <text x="180" y="125" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#F5A524">
        {legend.safe_label}
      </text>
      {/* Cropped-away strips */}
      <rect x="30" y="30" width="50" height="200" fill="#EF4444" opacity="0.12" />
      <rect x="280" y="30" width="50" height="200" fill="#EF4444" opacity="0.12" />
      <text x="55" y="140" textAnchor="middle" fontSize="9" fill="#EF4444">{legend.crop_label}</text>
      <text x="305" y="140" textAnchor="middle" fontSize="9" fill="#EF4444">{legend.crop_label}</text>

      {/* Legend box on right */}
      <g transform="translate(360, 40)">
        <rect width="14" height="14" fill="#F5A524" opacity="0.25" stroke={stroke} />
        <text x="22" y="11" fontSize="10" fill={text}>{legend.item_sensor}</text>

        <rect y="24" width="14" height="14" fill="none" stroke="#F5A524" strokeWidth="2" strokeDasharray="4 2" />
        <text x="22" y="35" fontSize="10" fill={text}>{legend.item_final}</text>

        <rect y="48" width="14" height="14" fill="#EF4444" opacity="0.35" />
        <text x="22" y="59" fontSize="10" fill={text}>{legend.item_crop}</text>

        <text x="0" y="90" fontSize="10" fill={muted}>{legend.rule_top}</text>
        <text x="0" y="105" fontSize="10" fill={muted}>{legend.rule_bottom}</text>
      </g>
    </svg>
  );
}

// -- Content (EN + ES) ------------------------------------------------------

const CONTENT = {
  en: {
    page_title: "Printing & Output",
    page_sub: "From pixels to paper — print sizes, DPI, aspect ratios, color management, and the full digital-to-print workflow.",
    download: "Download as PDF",
    generating: "Generating...",
    toc: "Table of Contents",
    sections: [
      "Print Size Reference",
      "Print Calculator",
      "DPI / PPI Explained",
      "Aspect Ratios & Cropping",
      "Digital Processing Workflow",
      "Framing & Matting",
      "Color Management",
      "File Format Guide",
      "Print Lab Checklist",
    ],
    table: {
      title: "Print Size Reference",
      intro:
        "Standard photo print sizes with the minimum pixel dimensions needed at three quality levels. 300 DPI is the photographic standard for close-viewing prints; 240 DPI is an acceptable compromise for most photo labs; 180 DPI is the minimum for large prints viewed from several feet away.",
      cols: ["Print Size", "300 DPI (Pro)", "240 DPI (Standard)", "180 DPI (Large / Distance)", "Megapixels @ 300", "Common Use"],
      note: "Megapixels shown are at 300 DPI. If your camera has fewer MP than required, drop to 240 DPI or print smaller.",
    },
    calculator: {
      title: "Print Calculator",
      intro: "Enter your image's pixel dimensions to see the maximum print size at each DPI tier.",
      label_w: "Image Width (px)",
      label_h: "Image Height (px)",
      at_300: "At 300 DPI (Pro)",
      at_240: "At 240 DPI (Standard)",
      at_180: "At 180 DPI (Large / Distance)",
      max_print: "Max Print Size",
      mp_label: "Total megapixels",
      tip_good:
        "You have room to spare — your image can be printed larger than expected at high quality.",
      tip_ok:
        "Acceptable for the sizes shown. For larger prints, drop to a lower DPI or use software upscaling carefully.",
      tip_low:
        "Your image is quite small. Limit yourself to the sizes shown at 180 DPI, or upscale with AI (Topaz Gigapixel, Photoshop Super Resolution).",
    },
    dpi: {
      title: "DPI / PPI Explained",
      intro:
        "DPI (dots per inch) and PPI (pixels per inch) describe how densely pixels are packed into each printed inch. More pixels per inch = more detail and smoother tones up close.",
      points: [
        "300 DPI is the 'photographic' standard — required for close-viewing prints like portraits or gallery pieces held at arm's length.",
        "240 DPI is a very common lab default. The difference is invisible for most prints viewed beyond 18 inches.",
        "180 DPI is fine for wall art and posters viewed from 3+ feet away. The eye cannot resolve the extra detail at that distance.",
        "Below 150 DPI, pixelation becomes visible — avoid unless you're making a large banner viewed from across a room.",
        "Rule of thumb: viewing distance (inches) x 2 = minimum DPI needed for that distance.",
      ],
    },
    aspect: {
      title: "Aspect Ratios & Cropping",
      intro:
        "Your camera captures a specific shape — 3:2 for DSLR/mirrorless, 4:3 for Micro Four Thirds and most phones, 1:1 for square formats. Print sizes don't all match, so cropping is often needed. Plan for it when composing.",
      points: [
        "4x6 print matches 3:2 camera ratio exactly — no cropping needed.",
        "5x7 and 8x10 are roughly 4:5 ratio — from a 3:2 DSLR file you will lose ~17% of the long side.",
        "11x14, 16x20, 20x30 are also 4:5 — same crop applies.",
        "Compose with 'breathing room' on the long edges — don't place critical elements near left/right edges on a 3:2 camera if you plan to print 8x10 or larger.",
        "In Lightroom/Photoshop use the 8x10 crop overlay before you click the shutter — mentally frame the final print, not the viewfinder.",
      ],
      legend: {
        frame_label: "3:2 Sensor (your full DSLR frame)",
        safe_label: "8x10 / 16x20 safe zone",
        crop_label: "Cropped out",
        item_sensor: "Your sensor (3:2)",
        item_final: "4:5 print safe zone",
        item_crop: "Will be cut off",
        rule_top: "~17% of the long edge is lost",
        rule_bottom: "when printing 8x10 from a DSLR",
      },
    },
    workflow: {
      title: "Digital Processing Workflow",
      intro: "Eight-step sequence from camera card to print-ready file.",
      steps: [
        { n: 1, name: "Import & Backup", text: "Copy RAW files to at least two drives (working drive + backup). Use Lightroom/Capture One to import with keywords, date, location." },
        { n: 2, name: "Cull (Rate & Reject)", text: "First pass — flag obvious keepers. Second pass — rate 1-5 stars or pick/reject. Only process keepers. Deleting aggressively is a skill." },
        { n: 3, name: "Global Adjustments", text: "White balance, exposure, contrast, highlights/shadows, clarity. Fix the whole image before touching local areas." },
        { n: 4, name: "Local Adjustments", text: "Dodging, burning, brush masks, subject enhancement. Raise the eyes, darken corners, cool down the background." },
        { n: 5, name: "Color & Tone", text: "HSL adjustments, split toning, color grading. Pick a signature look and apply consistently across the set." },
        { n: 6, name: "Sharpening & Noise Reduction", text: "Input sharpening on RAW, output sharpening for the print size. Always view at 100% when evaluating. Denoise before sharpening." },
        { n: 7, name: "Soft Proof", text: "Load your lab's ICC profile. Toggle soft-proof to see gamut-clipped colors before you send. Adjust saturated reds/blues that print out of gamut." },
        { n: 8, name: "Export for Print", text: "sRGB for web / most consumer labs; Adobe RGB or ProPhoto for pro labs. Resize to exact print pixels. TIFF or high-quality JPEG (quality 10-12)." },
      ],
    },
    framing: {
      title: "Framing & Matting",
      intro:
        "A great photo deserves great framing. The mat and frame shape how the image reads, protect it from damage, and should complement (not fight) the photograph.",
      cards: [
        { name: "Standard Mat Widths", img: PHOTOS.frame, desc: "2-3 in for 8x10, 3-4 in for 11x14, 4-5 in for 16x20 and larger. Wider mats = more 'museum' feel.", price: "$15-$80" },
        { name: "Glass vs Acrylic vs Non-Glare", img: PHOTOS.frame, desc: "Regular glass is cheapest, reflective. Non-glare has slight haze. Museum-grade anti-reflective is best, 4x the price. Acrylic is shatter-resistant (ship-safe).", price: "$20-$200" },
        { name: "Archival (Acid-Free) Mats", img: PHOTOS.frame, desc: "Standard mats contain acid that yellows prints over years. Archival/museum board is acid-free and lignin-free. Essential for prints you want to last.", price: "+$10-$40" },
        { name: "Float Mounting", img: PHOTOS.frame, desc: "Print sits on top of the mat (no cutout) with visible edges. Modern look for fine-art prints with deckle edges or signatures.", price: "Custom" },
      ],
      tips: [
        "Frame depth: 0.75 in (1.9 cm) minimum. Deeper frames add presence — 1.25-2 in for statement pieces.",
        "Print goes behind the mat, held in place by the mat's bevel cut. Never tape directly to the print.",
        "Mat color: off-white / warm white is safe for most photos. Black mat adds drama. Colored mats rarely work.",
        "Hang height: center of the photo at 57 inches (eye level) in most galleries.",
        "Lighting: LED at 3000-4000K, CRI 90+. Angle 30 degrees from the wall to minimize glare.",
      ],
    },
    color: {
      title: "Color Management",
      intro:
        "Color on your camera → your monitor → the lab's printer must stay consistent. This needs calibration and the right color space.",
      points: [
        "Calibrated monitor: Use an X-Rite or Calibrite display calibrator once a month. Target D65 white point, 120 cd/m2 luminance, gamma 2.2.",
        "Working color space: Adobe RGB or ProPhoto RGB in-camera and in Lightroom. Save sRGB conversion for final export.",
        "Paper profiles (ICC): Download from your lab or paper manufacturer. Load in Photoshop/Lightroom for soft-proofing.",
        "Paper types: Glossy (vibrant colors, visible reflections), Luster/Satin (compromise, great for portraits), Matte (artistic feel, less contrast), Fine-Art (cotton rag, museum-grade).",
        "Soft-proofing in Lightroom: Develop → Soft Proofing panel → select your paper profile → check 'Show Out-of-Gamut Warnings' (red overlay on colors your printer cannot reproduce).",
        "Monitor vs print expectation: prints always look slightly darker. Preview your image 1 stop brighter than you want the print to be, or lower monitor brightness to 80-100 cd/m2.",
      ],
    },
    formats: {
      title: "File Format Guide",
      intro: "Each format has a specific role in the workflow — know which to use when.",
      rows: [
        { fmt: "RAW (.CR3, .NEF, .ARW, .RAF)", use: "Original camera file. Edit in Lightroom/Capture One. Do NOT send to lab.", tip: "Always shoot RAW for important work." },
        { fmt: "TIFF (.tif)", use: "Lossless master file after editing. Send to pro labs. Supports 16-bit, layers, ICC profiles.", tip: "Best for pro-quality prints 16x20+." },
        { fmt: "JPEG (.jpg)", use: "Universal print format. Quality 10-12 (95-100%) for lab prints. Quality 8 for web/email.", tip: "Use sRGB color space for consumer labs." },
        { fmt: "PNG (.png)", use: "Web graphics, screenshots with text. NOT for photo prints.", tip: "Avoid for print." },
        { fmt: "PSD (.psd)", use: "Photoshop working file with layers, masks, smart objects. Archive your masters here.", tip: "Keep PSD for the archive, export JPEG/TIFF for delivery." },
        { fmt: "DNG (.dng)", use: "Adobe's lossless RAW. Same as RAW but portable across software.", tip: "Optional — most shooters stay with camera-native RAW." },
      ],
    },
    checklist: {
      title: "Print Lab Checklist",
      intro: "Send the right file the first time. Every lab asks for the same 8 specs — have them ready.",
      items: [
        "File format: TIFF for pro labs, JPEG quality 10-12 for consumer labs.",
        "Color space: sRGB for consumer labs, Adobe RGB or the lab's ICC profile for pro labs.",
        "Bit depth: 16-bit for TIFF, 8-bit for JPEG.",
        "Resolution: match the pixel dimensions to the target DPI (check the Print Size Reference table).",
        "Print size: match the exact inches you want — 5x7 and 5x7.5 are NOT the same file.",
        "Paper type: glossy / luster / matte / fine-art — confirm paper before uploading.",
        "Borderless vs with border: most labs add 1/8 in white border by default. Specify 'no border' or 'full bleed' if needed.",
        "File name: include client / project / size (e.g. 'Smith_Wedding_16x20_Luster.tif'). Helps lab support if issues arise.",
      ],
    },
    footer_note:
      "A great print is a conversation between photographer, lab, and paper. Test-print small (5x7) before committing to a 20x30, calibrate your monitor, and trust your eyes at arm's length.",
  },
  es: {
    page_title: "Impresión y Salida",
    page_sub: "De píxeles a papel — tamaños de impresión, DPI, proporciones, gestion de color y el flujo completo de digital a impreso.",
    download: "Descargar como PDF",
    generating: "Generando...",
    toc: "Tabla de Contenido",
    sections: [
      "Referencia de Tamaños",
      "Calculadora de Impresión",
      "DPI / PPI Explicado",
      "Proporciones y Recorte",
      "Flujo de Procesado Digital",
      "Enmarcado y Matting",
      "Gestion de Color",
      "Guía de Formatos",
      "Lista para el Laboratorio",
    ],
    table: {
      title: "Referencia de Tamaños de Impresión",
      intro:
        "Tamaños estándar de fotografía impresa con las dimensiones mínimas en píxeles para tres niveles de calidad. 300 DPI es el estándar fotográfico para impresiones vistas de cerca; 240 DPI es un compromiso aceptable para la mayoría de laboratorios; 180 DPI es el mínimo para impresiones grandes vistas a varios metros.",
      cols: ["Tamaño", "300 DPI (Pro)", "240 DPI (Estándar)", "180 DPI (Grande / Distancia)", "MP @ 300", "Uso Común"],
      note: "Los megapíxeles mostrados son a 300 DPI. Si tu cámara tiene menos MP, baja a 240 DPI o imprime más pequeño.",
    },
    calculator: {
      title: "Calculadora de Impresión",
      intro: "Ingresa las dimensiones en píxeles de tu imagen para ver el tamaño máximo de impresión en cada nivel de DPI.",
      label_w: "Ancho de Imagen (px)",
      label_h: "Altura de Imagen (px)",
      at_300: "A 300 DPI (Pro)",
      at_240: "A 240 DPI (Estándar)",
      at_180: "A 180 DPI (Grande / Distancia)",
      max_print: "Tamaño Max. de Impresión",
      mp_label: "Megapíxeles totales",
      tip_good:
        "Tienes espacio de sobra — tu imagen se puede imprimir más grande de lo esperado con alta calidad.",
      tip_ok:
        "Aceptable para los tamaños mostrados. Para impresiones mayores, baja el DPI o usa upscaling con cuidado.",
      tip_low:
        "Tu imagen es pequeña. Limitate a los tamaños mostrados a 180 DPI, o usa upscaling con IA (Topaz Gigapixel, Super Resolution de Photoshop).",
    },
    dpi: {
      title: "DPI / PPI Explicado",
      intro:
        "DPI (puntos por pulgada) y PPI (píxeles por pulgada) describen que tan densamente se empacan los píxeles en cada pulgada impresa. Más píxeles por pulgada = más detalle y tonos más suaves de cerca.",
      points: [
        "300 DPI es el estándar fotográfico — requerido para impresiones vistas de cerca como retratos o piezas de galería.",
        "240 DPI es un default muy común en laboratorios. La diferencia es invisible para la mayoría de impresiones vistas a más de 45 cm.",
        "180 DPI está bien para arte de pared y posters vistos a 1 m o más. El ojo no resuelve el detalle adicional a esa distancia.",
        "Debajo de 150 DPI se nota pixelación — evitalo salvo que hagas un gran banner visto desde el otro lado de un cuarto.",
        "Regla: distancia de visualización (pulgadas) x 2 = DPI mínimo necesario para esa distancia.",
      ],
    },
    aspect: {
      title: "Proporciones y Recorte",
      intro:
        "Tu cámara captura una forma especifica — 3:2 para DSLR/mirrorless, 4:3 para Micro Four Thirds y teléfonos, 1:1 para formatos cuadrados. Los tamaños de impresión no coinciden todos, así que a menudo se recorta. Planealo al componer.",
      points: [
        "Impresión 4x6 coincide exactamente con 3:2 — no requiere recorte.",
        "5x7 y 8x10 son aprox. 4:5 — de un archivo 3:2 pierdes ~17% del lado largo.",
        "11x14, 16x20, 20x30 son también 4:5 — mismo recorte aplica.",
        "Compon con 'aire' en los bordes largos — no coloques elementos criticos cerca de izq/der si planeas imprimir 8x10 o mayor.",
        "En Lightroom/Photoshop usa el overlay de recorte 8x10 antes del disparo — encuadra mentalmente la impresión final, no el visor.",
      ],
      legend: {
        frame_label: "Sensor 3:2 (cuadro DSLR completo)",
        safe_label: "Zona segura 8x10 / 16x20",
        crop_label: "Recortado",
        item_sensor: "Tu sensor (3:2)",
        item_final: "Zona segura 4:5",
        item_crop: "Se cortara",
        rule_top: "~17% del lado largo se pierde",
        rule_bottom: "al imprimir 8x10 desde DSLR",
      },
    },
    workflow: {
      title: "Flujo de Procesado Digital",
      intro: "Secuencia de 8 pasos de tarjeta de cámara a archivo listo para imprimir.",
      steps: [
        { n: 1, name: "Importar y Respaldar", text: "Copia los RAW a al menos dos discos (trabajo + respaldo). Usa Lightroom/Capture One para importar con keywords, fecha, ubicación." },
        { n: 2, name: "Selección (Rate & Reject)", text: "Primera pasada — marca los buenos obvios. Segunda pasada — califica 1-5 estrellas o pick/reject. Solo procesa los elegidos. Borrar agresivamente es una habilidad." },
        { n: 3, name: "Ajustes Globales", text: "Balance de blancos, exposición, contraste, altas/sombras, claridad. Arregla la imagen completa antes de tocar areas locales." },
        { n: 4, name: "Ajustes Locales", text: "Dodging, burning, mascaras de pincel, realce del sujeto. Sube los ojos, oscurece las esquinas, enfria el fondo." },
        { n: 5, name: "Color y Tono", text: "Ajustes HSL, split toning, color grading. Elige una firma visual y aplicala consistente en toda la serie." },
        { n: 6, name: "Enfoque y Reducción de Ruido", text: "Enfoque de entrada en RAW, enfoque de salida para el tamaño de impresión. Siempre ve al 100% al evaluar. Reduce ruido antes de enfocar." },
        { n: 7, name: "Soft Proof", text: "Carga el perfil ICC de tu laboratorio. Activa el soft-proof para ver colores fuera de gama antes de enviar. Ajusta rojos/azules saturados que imprimen fuera de gama." },
        { n: 8, name: "Exportar para Impresión", text: "sRGB para web / labs de consumidor; Adobe RGB o ProPhoto para labs pro. Redimensiona a los píxeles exactos. TIFF o JPEG alta calidad (calidad 10-12)." },
      ],
    },
    framing: {
      title: "Enmarcado y Matting",
      intro:
        "Una gran foto merece un gran marco. El mat y el marco moldean como se lee la imagen, la protegen de dano, y deben complementarla (no pelear con ella).",
      cards: [
        { name: "Anchos Estándar de Mat", img: PHOTOS.frame, desc: "5-7.5 cm para 8x10, 7.5-10 cm para 11x14, 10-12.5 cm para 16x20 y mayores. Mats más anchos = sensación más 'de museo'.", price: "$15-$80" },
        { name: "Vidrio vs Acrilico vs Antirreflejos", img: PHOTOS.frame, desc: "Vidrio normal es más barato, reflectivo. Antirreflejo tiene ligera neblina. Museo antirreflejos es el mejor, 4x el precio. Acrilico es a prueba de roturas (seguro para envio).", price: "$20-$200" },
        { name: "Mats Libres de Acido", img: PHOTOS.frame, desc: "Los mats estándar contienen acido que amarilla los prints con los años. El cartulina de archivo/museo es libre de acido y lignina. Esencial para prints que duren.", price: "+$10-$40" },
        { name: "Float Mounting", img: PHOTOS.frame, desc: "El print va sobre el mat (sin corte) con bordes visibles. Look moderno para prints de arte fino con bordes deckle o firmas.", price: "Personalizado" },
      ],
      tips: [
        "Profundidad de marco: 1.9 cm mínimo. Marcos más profundos dan presencia — 3-5 cm para piezas destacadas.",
        "El print va detrás del mat, sostenido por el corte bisel. Nunca pegues cinta directo al print.",
        "Color del mat: off-white / warm white es seguro para la mayoría. Negro añade drama. Los colores rara vez funcionan.",
        "Altura de colgado: centro de la foto a 145 cm (altura de ojos) en la mayoría de galerias.",
        "Iluminación: LED de 3000-4000K, CRI 90+. Ángulo 30° respecto al muro para minimizar reflejos.",
      ],
    },
    color: {
      title: "Gestion de Color",
      intro:
        "El color en tu cámara → tu monitor → la impresora del laboratorio debe mantenerse consistente. Requiere calibración y el espacio de color correcto.",
      points: [
        "Monitor calibrado: Usa un calibrador X-Rite o Calibrite una vez al mes. Objetivo D65, luminancia 120 cd/m2, gamma 2.2.",
        "Espacio de color de trabajo: Adobe RGB o ProPhoto RGB en cámara y en Lightroom. Conversión a sRGB solo al exportar final.",
        "Perfiles de papel (ICC): Descargalos de tu laboratorio o del fabricante del papel. Cargalos en Photoshop/Lightroom para soft-proofing.",
        "Tipos de papel: Glossy (colores vibrantes, reflejos visibles), Luster/Satin (compromiso, excelente para retratos), Matte (sensación artística, menos contraste), Fine-Art (cotton rag, grado museo).",
        "Soft-proofing en Lightroom: Develop → panel Soft Proofing → selecciona tu perfil de papel → activa 'Show Out-of-Gamut Warnings' (overlay rojo en colores que tu impresora no puede reproducir).",
        "Monitor vs expectativa de impresión: los prints siempre se ven un poco más oscuros. Previsualiza tu imagen 1 paso más brillante de lo que quieres el print, o baja la luminancia del monitor a 80-100 cd/m2.",
      ],
    },
    formats: {
      title: "Guía de Formatos de Archivo",
      intro: "Cada formato tiene un rol específico en el flujo — saber cual usar cuando.",
      rows: [
        { fmt: "RAW (.CR3, .NEF, .ARW, .RAF)", use: "Archivo original de la cámara. Edita en Lightroom/Capture One. NO lo envies al laboratorio.", tip: "Siempre dispara RAW para trabajo importante." },
        { fmt: "TIFF (.tif)", use: "Maestro sin pérdida después de edición. Envía a labs pro. Soporta 16-bit, capas, perfiles ICC.", tip: "Mejor para prints pro 16x20+." },
        { fmt: "JPEG (.jpg)", use: "Formato universal de impresión. Calidad 10-12 (95-100%) para labs. Calidad 8 para web/email.", tip: "Usa sRGB para labs de consumidor." },
        { fmt: "PNG (.png)", use: "Gráficos web, capturas con texto. NO para prints de foto.", tip: "Evitalo para impresión." },
        { fmt: "PSD (.psd)", use: "Archivo de trabajo de Photoshop con capas, mascaras, smart objects. Archiva tus maestros aquí.", tip: "Guarda PSD para archivo, exporta JPEG/TIFF para entrega." },
        { fmt: "DNG (.dng)", use: "RAW sin pérdida de Adobe. Igual que RAW pero portable entre software.", tip: "Opcional — la mayoría se queda con RAW nativo de cámara." },
      ],
    },
    checklist: {
      title: "Lista para el Laboratorio",
      intro: "Envía el archivo correcto la primera vez. Cada lab pide las mismas 8 especificaciones — tenlas listas.",
      items: [
        "Formato: TIFF para labs pro, JPEG calidad 10-12 para labs de consumidor.",
        "Espacio de color: sRGB para consumidor, Adobe RGB o el ICC del lab para pro.",
        "Profundidad de bit: 16-bit para TIFF, 8-bit para JPEG.",
        "Resolución: empareja las dimensiones en píxeles con el DPI objetivo (revisa la tabla de Referencia).",
        "Tamaño de impresión: empareja las pulgadas exactas — 5x7 y 5x7.5 NO son el mismo archivo.",
        "Tipo de papel: glossy / luster / matte / fine-art — confirma antes de subir.",
        "Con / sin borde: la mayoría añade borde blanco de 3 mm por default. Especifica 'sin borde' o 'full bleed' si lo necesitas.",
        "Nombre del archivo: incluye cliente / proyecto / tamaño (ej. 'Smith_Boda_16x20_Luster.tif'). Ayuda si hay problemas.",
      ],
    },
    footer_note:
      "Un gran print es una conversación entre fotógrafo, laboratorio y papel. Haz pruebas pequeñas (5x7) antes de comprometer un 20x30, calibra tu monitor, y confia en tus ojos a distancia de brazo.",
  },
};

// -- Print Calculator hook ------------------------------------------------

function PrintCalculator({ lang, c }) {
  const [px_w, setPxW] = useState(6000);
  const [px_h, setPxH] = useState(4000);

  const results = useMemo(() => {
    const mp = Math.round((px_w * px_h) / 100000) / 10;
    return {
      mp,
      at_300: { w: px_w / 300, h: px_h / 300 },
      at_240: { w: px_w / 240, h: px_h / 240 },
      at_180: { w: px_w / 180, h: px_h / 180 },
    };
  }, [px_w, px_h]);

  const fmt = (n) => `${n.toFixed(1)}"`;

  // Tip based on total pixels
  let tipKey = "tip_ok";
  if (px_w * px_h >= 30_000_000) tipKey = "tip_good";
  else if (px_w * px_h < 6_000_000) tipKey = "tip_low";

  return (
    <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6" data-testid="print-calculator">
      <div className="flex items-center gap-2 mb-2">
        <Calculator weight="fill" className="w-5 h-5 text-[#F5A524]" />
        <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.calculator.title}</h2>
      </div>
      <p className="text-[#A1A1AA] text-sm leading-relaxed mb-5">{c.calculator.intro}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div>
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em] font-semibold mb-2 block">
            {c.calculator.label_w}
          </Label>
          <Input
            type="number"
            min="100"
            step="100"
            value={px_w}
            onChange={(e) => setPxW(Math.max(100, parseInt(e.target.value || "0", 10)))}
            className="bg-[#0A0A0A] border-white/10 text-white font-['JetBrains_Mono']"
            data-testid="calc-width"
          />
        </div>
        <div>
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em] font-semibold mb-2 block">
            {c.calculator.label_h}
          </Label>
          <Input
            type="number"
            min="100"
            step="100"
            value={px_h}
            onChange={(e) => setPxH(Math.max(100, parseInt(e.target.value || "0", 10)))}
            className="bg-[#0A0A0A] border-white/10 text-white font-['JetBrains_Mono']"
            data-testid="calc-height"
          />
        </div>
      </div>

      <div className="flex items-baseline justify-between mb-4">
        <span className="text-[#71717A] text-xs uppercase tracking-widest">{c.calculator.mp_label}</span>
        <span className="font-['JetBrains_Mono'] text-2xl font-bold text-[#F5A524]" data-testid="calc-mp">
          {results.mp} MP
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {[
          { k: "at_300", label: c.calculator.at_300, accent: "text-emerald-400" },
          { k: "at_240", label: c.calculator.at_240, accent: "text-blue-400" },
          { k: "at_180", label: c.calculator.at_180, accent: "text-purple-400" },
        ].map((tier) => (
          <div key={tier.k} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
            <p className={`text-xs uppercase tracking-widest font-semibold mb-2 ${tier.accent}`}>{tier.label}</p>
            <p className="text-[#71717A] text-xs mb-1">{c.calculator.max_print}</p>
            <p className="font-['JetBrains_Mono'] text-lg font-bold text-white" data-testid={`calc-${tier.k}`}>
              {fmt(results[tier.k].w)} × {fmt(results[tier.k].h)}
            </p>
          </div>
        ))}
      </div>

      <div className="bg-[#F5A524]/5 border border-[#F5A524]/20 rounded-lg p-3 flex items-start gap-2">
        <Info weight="fill" className="w-4 h-4 text-[#F5A524] mt-0.5 shrink-0" />
        <p className="text-[#D4D4D8] text-sm leading-relaxed">{c.calculator[tipKey]}</p>
      </div>
    </div>
  );
}

// -- Main page --------------------------------------------------------------

export default function PrintingPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);

  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `printing-and-output-${lang}.pdf`,
    [lang]
  );

  const formatMP = (w, h) => {
    const mp = (w * h) / 1_000_000;
    return mp >= 10 ? Math.round(mp) : mp.toFixed(1);
  };

  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black"
      data-testid="printing-page"
    >
      {/* Screen (dark) UI */}
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          {/* Header */}
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="printing-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>
          <motion.div variants={itemAnim}><MirrorlessNote topic="printing" /></motion.div>

          {/* Hero */}
          <motion.div variants={itemAnim} className="relative h-52 sm:h-64 rounded-2xl overflow-hidden border border-white/10">
            <img src={PHOTOS.hero} alt="printing" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
          </motion.div>

          {/* 1. Reference Table */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Ruler weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.table.title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.table.intro}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    {c.table.cols.map((col, i) => (
                      <th
                        key={i}
                        className="text-left text-[#71717A] text-xs uppercase tracking-wider font-semibold py-2 pr-3"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PRINT_SIZES.map((size) => (
                    <tr key={`${size.w}x${size.h}`} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 pr-3 text-white font-semibold font-['JetBrains_Mono']">
                        {size.w}" × {size.h}"
                      </td>
                      <td className="py-2 pr-3 text-[#D4D4D8] font-['JetBrains_Mono']">
                        {pxAt(300, size.w).toLocaleString()} × {pxAt(300, size.h).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 text-[#A1A1AA] font-['JetBrains_Mono']">
                        {pxAt(240, size.w).toLocaleString()} × {pxAt(240, size.h).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 text-[#71717A] font-['JetBrains_Mono']">
                        {pxAt(180, size.w).toLocaleString()} × {pxAt(180, size.h).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 text-[#F5A524] font-['JetBrains_Mono']">
                        {formatMP(pxAt(300, size.w), pxAt(300, size.h))}
                      </td>
                      <td className="py-2 text-[#A1A1AA] text-xs">
                        {lang === "es" ? size.common_es : size.common_en}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[#71717A] text-xs italic mt-4">{c.table.note}</p>
          </motion.div>

          {/* 2. Print Calculator */}
          <motion.div variants={itemAnim}>
            <PrintCalculator lang={lang} c={c} />
          </motion.div>

          {/* 3. DPI Explained */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Info weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.dpi.title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.dpi.intro}</p>
            <ul className="space-y-2">
              {c.dpi.points.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* 4. Aspect & Cropping with SVGs */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Crop weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.aspect.title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.aspect.intro}</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
                <AspectRatioDiagram dark />
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
                <CropDiagram dark legend={c.aspect.legend} />
              </div>
            </div>
            <ul className="space-y-2 mt-4">
              {c.aspect.points.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* 5. Workflow */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Stack weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.workflow.title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.workflow.intro}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {c.workflow.steps.map((step) => (
                <div key={step.n} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded-full bg-[#F5A524] text-[#0A0A0A] flex items-center justify-center text-xs font-bold">
                      {step.n}
                    </span>
                    <h3 className="text-white font-medium text-sm">{step.name}</h3>
                  </div>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* 6. Framing */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.framing.title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.framing.intro}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {c.framing.cards.map((card, i) => (
                <div
                  key={i}
                  className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden"
                  data-testid={`framing-card-${i}`}
                >
                  <div className="relative h-32 bg-[#18181B]">
                    <img src={card.img} alt={card.name} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 to-transparent" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-medium text-sm mb-1">{card.name}</h3>
                    <p className="text-[#A1A1AA] text-xs leading-relaxed mb-2">{card.desc}</p>
                    <p className="text-[#F5A524] text-xs font-['JetBrains_Mono']">{card.price}</p>
                  </div>
                </div>
              ))}
            </div>
            <ul className="space-y-2 mt-4">
              {c.framing.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{tip}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* 7. Color Management */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Palette weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.color.title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.color.intro}</p>
            <div className="mb-4 relative h-40 rounded-xl overflow-hidden border border-white/10">
              <img src={PHOTOS.monitor} alt="calibrated monitor" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 to-transparent" />
            </div>
            <ul className="space-y-2">
              {c.color.points.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* 8. File Formats */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <FileImage weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.formats.title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.formats.intro}</p>
            <div className="space-y-3">
              {c.formats.rows.map((row, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                  <p className="text-[#F5A524] font-['JetBrains_Mono'] text-xs font-semibold mb-1">{row.fmt}</p>
                  <p className="text-[#D4D4D8] text-sm mb-1">{row.use}</p>
                  <p className="text-[#71717A] text-xs italic">{row.tip}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* 9. Lab Checklist */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <CheckSquare weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.checklist.title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.checklist.intro}</p>
            <div className="mb-4 relative h-32 rounded-xl overflow-hidden border border-white/10">
              <img src={PHOTOS.lab} alt="print lab" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 to-transparent" />
            </div>
            <ol className="space-y-2">
              {c.checklist.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ol>
          </motion.div>

          <motion.p variants={itemAnim} className="text-[#A1A1AA] text-sm italic text-center max-w-3xl mx-auto leading-relaxed">
            "{c.footer_note}"
          </motion.p>
        </motion.div>
      </div>

      {/* Print / PDF (white) */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="printing-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>

        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">{c.toc}</h2>
          <div className="grid grid-cols-2 gap-x-6 text-sm">
            {c.sections.map((s, i) => (
              <div key={i} className="text-gray-700">
                <span className="text-gray-400 mr-1">{i + 1}.</span>
                {s}
              </div>
            ))}
          </div>
        </div>

        {/* Reference Table */}
        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">1. {c.table.title}</h2>
          <p className="text-sm text-gray-700 mb-3">{c.table.intro}</p>
          <table className="w-full text-xs border-collapse mb-2">
            <thead>
              <tr className="bg-gray-100">
                {c.table.cols.map((col, i) => (
                  <th key={i} className="border border-gray-300 p-2 text-left">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PRINT_SIZES.map((size) => (
                <tr key={`p-${size.w}x${size.h}`}>
                  <td className="border border-gray-300 p-2 font-semibold font-mono">{size.w}" × {size.h}"</td>
                  <td className="border border-gray-300 p-2 font-mono">{pxAt(300, size.w).toLocaleString()} × {pxAt(300, size.h).toLocaleString()}</td>
                  <td className="border border-gray-300 p-2 font-mono">{pxAt(240, size.w).toLocaleString()} × {pxAt(240, size.h).toLocaleString()}</td>
                  <td className="border border-gray-300 p-2 font-mono">{pxAt(180, size.w).toLocaleString()} × {pxAt(180, size.h).toLocaleString()}</td>
                  <td className="border border-gray-300 p-2 font-mono">{formatMP(pxAt(300, size.w), pxAt(300, size.h))}</td>
                  <td className="border border-gray-300 p-2">{lang === "es" ? size.common_es : size.common_en}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-500 italic">{c.table.note}</p>
        </div>

        {/* DPI */}
        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">3. {c.dpi.title}</h2>
          <p className="text-sm text-gray-700 mb-2">{c.dpi.intro}</p>
          <ul className="list-disc ml-5 space-y-1">
            {c.dpi.points.map((p, i) => (
              <li key={i} className="text-xs text-gray-700">{p}</li>
            ))}
          </ul>
        </div>

        {/* Aspect */}
        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">4. {c.aspect.title}</h2>
          <p className="text-sm text-gray-700 mb-3">{c.aspect.intro}</p>
          <div className="grid grid-cols-1 gap-4 mb-3">
            <AspectRatioDiagram />
            <CropDiagram legend={c.aspect.legend} />
          </div>
          <ul className="list-disc ml-5 space-y-1">
            {c.aspect.points.map((p, i) => (
              <li key={i} className="text-xs text-gray-700">{p}</li>
            ))}
          </ul>
        </div>

        {/* Workflow */}
        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">5. {c.workflow.title}</h2>
          <p className="text-sm text-gray-700 mb-3">{c.workflow.intro}</p>
          <ol className="list-decimal ml-5 space-y-2">
            {c.workflow.steps.map((step) => (
              <li key={step.n} className="text-xs text-gray-700">
                <strong>{step.name}:</strong> {step.text}
              </li>
            ))}
          </ol>
        </div>

        {/* Framing */}
        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">6. {c.framing.title}</h2>
          <p className="text-sm text-gray-700 mb-3">{c.framing.intro}</p>
          {c.framing.cards.map((card, i) => (
            <p key={i} className="text-xs text-gray-700 mb-1">
              <strong>{card.name}</strong> ({card.price}): {card.desc}
            </p>
          ))}
          <ul className="list-disc ml-5 space-y-1 mt-2">
            {c.framing.tips.map((tip, i) => (
              <li key={i} className="text-xs text-gray-700">{tip}</li>
            ))}
          </ul>
        </div>

        {/* Color */}
        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">7. {c.color.title}</h2>
          <p className="text-sm text-gray-700 mb-2">{c.color.intro}</p>
          <ul className="list-disc ml-5 space-y-1">
            {c.color.points.map((p, i) => (
              <li key={i} className="text-xs text-gray-700">{p}</li>
            ))}
          </ul>
        </div>

        {/* Formats */}
        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">8. {c.formats.title}</h2>
          <p className="text-sm text-gray-700 mb-3">{c.formats.intro}</p>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-left">{lang === "es" ? "Formato" : "Format"}</th>
                <th className="border border-gray-300 p-2 text-left">{lang === "es" ? "Uso" : "Use"}</th>
                <th className="border border-gray-300 p-2 text-left">{lang === "es" ? "Consejo" : "Tip"}</th>
              </tr>
            </thead>
            <tbody>
              {c.formats.rows.map((row, i) => (
                <tr key={i}>
                  <td className="border border-gray-300 p-2 font-mono">{row.fmt}</td>
                  <td className="border border-gray-300 p-2">{row.use}</td>
                  <td className="border border-gray-300 p-2 italic text-gray-600">{row.tip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Lab Checklist */}
        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">9. {c.checklist.title}</h2>
          <p className="text-sm text-gray-700 mb-2">{c.checklist.intro}</p>
          <ol className="list-decimal ml-5 space-y-1">
            {c.checklist.items.map((item, i) => (
              <li key={i} className="text-xs text-gray-700">{item}</li>
            ))}
          </ol>
        </div>

        <p className="text-sm text-gray-600 italic text-center max-w-2xl mx-auto leading-relaxed mt-8">
          "{c.footer_note}"
        </p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
